import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type KanbanSortKey = 'default' | 'priority' | 'due_date' | 'title' | 'created';
export type SortDirection = 'asc' | 'desc';
export type ColumnSort = { sortBy: KanbanSortKey; sortDir: SortDirection };

interface TaskFilters {
  assigneeFilter: string; // 'all', 'me', or user ID
  searchQuery: string;
  hideDone: boolean;
  /** Per-status sort config. Status keys map to { sortBy, sortDir } when the
   * user has set an explicit sort for that column; absent keys mean "default
   * order" for that column. */
  kanbanColumnSorts: Record<string, ColumnSort>;
}

interface FilterState {
  taskFilters: TaskFilters;
  getTaskFilters: () => TaskFilters;
  setTaskFilters: (filters: Partial<TaskFilters>) => void;
  resetTaskFilters: () => void;
}

const DEFAULT_TASK_FILTERS: TaskFilters = {
  assigneeFilter: 'all',
  searchQuery: '',
  hideDone: false,
  kanbanColumnSorts: {},
};

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      taskFilters: DEFAULT_TASK_FILTERS,
      getTaskFilters: () => get().taskFilters,
      setTaskFilters: (filters) =>
        set((state) => ({
          taskFilters: { ...state.taskFilters, ...filters },
        })),
      resetTaskFilters: () =>
        set(() => ({
          taskFilters: DEFAULT_TASK_FILTERS,
        })),
    }),
    {
      name: 'littlebooker-task-filters',
      // Merge persisted state on top of defaults so adding a new field (e.g.
      // kanbanSortBy) doesn't leave returning users with `undefined` for it.
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<FilterState>),
        taskFilters: {
          ...current.taskFilters,
          ...((persisted as { taskFilters?: Partial<TaskFilters> })?.taskFilters ?? {}),
        },
      }),
    }
  )
);
